import { useParams, Link } from 'react-router-dom';
import PageLayout from '@/components/feature/PageLayout';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);

  if (!category) {
    return (
      <PageLayout>
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
          <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-4">分类未找到</h2>
          <p className="text-foreground-600 mb-6">抱歉，您访问的分类不存在。</p>
          <Link to="/" className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap">
            返回首页
          </Link>
        </div>
      </PageLayout>
    );
  }

  const categoryArticles = articles.filter((a) => a.categorySlug === slug);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 黑料聚合网`,
    description: category.description,
    url: `https://51heiliao.com/category/${slug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: categoryArticles.map((article, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `https://51heiliao.com/article/${article.slug}`,
        name: article.title,
      })),
    },
  };

  return (
    <PageLayout>
      <SEO
        title={`${category.name} - 黑料聚合网 | ${category.name}最新内容合集`}
        description={category.description}
        keywords={`${category.name},黑料,51黑料网,91黑料网,17黑料网,黑料718,${category.name}最新`}
        canonicalUrl={`https://51heiliao.com/category/${slug}`}
        jsonLd={jsonLd}
      />
      <Breadcrumb
        items={[
          { label: '首页', href: '/' },
          { label: category.name },
        ]}
      />

      <section className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-3">
            <span className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center">
              <i className={`${category.icon} text-xl text-accent-700`}></i>
            </span>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
              {category.name}
            </h1>
          </div>
          <p className="text-foreground-600 text-sm md:text-base max-w-2xl">
            {category.description}
          </p>
        </div>

        <div className="flex flex-wrap gap-2 mb-8">
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors duration-200 whitespace-nowrap ${
                cat.slug === slug
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              {cat.name}
            </Link>
          ))}
        </div>

        {categoryArticles.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-product-shop="">
            {categoryArticles.map((article) => (
              <ArticleCard key={article.slug} {...article} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <span className="w-16 h-16 rounded-2xl bg-background-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-inbox-line text-2xl text-foreground-400"></i>
            </span>
            <p className="text-foreground-600">该分类下暂无文章</p>
          </div>
        )}
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <div className="bg-background-100 rounded-xl p-6 md:p-8">
          <h3 className="text-sm font-semibold text-foreground-900 mb-3">相关推荐</h3>
          <div className="flex flex-wrap gap-2">
            {categories
              .filter((c) => c.slug !== slug)
              .map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="px-4 py-2 bg-background-50 rounded-lg text-sm text-foreground-600 hover:text-primary-500 hover:bg-primary-50 transition-colors border border-background-200/70 whitespace-nowrap flex items-center gap-2"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className={`${cat.icon} text-xs`}></i>
                  </span>
                  {cat.name}
                </Link>
              ))}
          </div>
        </div>
      </section>
    </PageLayout>
  );
}